// @flow
import {Cookies} from 'react-cookie';
import {all, call, fork, put, takeEvery} from 'redux-saga/effects';

import {apiClient, request} from '../../../shared/api';

import {defaultConfigurations} from '../../../shared/configUtils';
import {folderConfigurations} from '../../../shared/folderUtils';

import {
    CHECK_AUTH_TYPE,
    FORGET_PASSWORD,
    LOGIN_USER,
    LOGOUT_USER,
    REGISTER_LINKED_ACCOUNT,
    REGISTER_USER,
    FETCH_USER_PROFILE,
    INIT_AUTH,
} from './constants';

import {
    checkAuthTypeFailed,
    checkAuthTypeSuccess,
    forgetPasswordFailed,
    forgetPasswordSuccess,
    loginUserFailed,
    loginUserSuccess,
    registerUserFailed,
    registerUserSuccess,
    fetchUserProfileSuccess,
    fetchUserProfileFailed,
    initAuthSuccess,
    initAuthFailed,
} from './actions';
import {api} from "../../../shared/constants/api";
import {CACHE_KEYS} from '../../../shared/constants/cacheKey';

/**
 * Sets the session cookie (lightweight — no token, just a flag for UI state persistence)
 */
const setSession = user => {
    let cookies = new Cookies();
    if (user) cookies.set(CACHE_KEYS.USER, JSON.stringify(user), {path: '/'});
    else cookies.remove(CACHE_KEYS.USER, {path: '/'});
};

/**
 * Fetch user profile from /me endpoint
 */
function* fetchUserProfile() {
    try {
        const response = yield call([apiClient, apiClient.list], 'api/v2/auth/me');
        const profile = (response && response.data && !response.error) ? response.data
            : (response && !response.error) ? response : null;
        if (profile) {
            if (profile.permissions !== undefined) {
                sessionStorage.setItem(CACHE_KEYS.USER_PERMISSION, String(profile.permissions));
            }
            yield put(fetchUserProfileSuccess(profile));
        } else {
            yield put(fetchUserProfileFailed('Failed to fetch profile'));
        }
    } catch (error) {
        yield put(fetchUserProfileFailed('Failed to fetch profile'));
    }
}

/**
 * Initialize auth on app startup — attempt silent refresh
 */
function* initAuth() {
    try {
        const newToken = yield call([apiClient, apiClient.refreshAccessToken]);
        if (newToken) {
            yield call(fetchUserProfile);
            defaultConfigurations();
            folderConfigurations();
            yield put(initAuthSuccess());
        } else {
            setSession(null);
            yield put(initAuthFailed());
        }
    } catch (error) {
        setSession(null);
        yield put(initAuthFailed());
    }
}

function* checkAuthType({payload: {username}}) {
    const options = {
        method: 'GET',
    };
    const response = yield call(request, api.authType(username), options);
    if (response.error) {
        yield put(checkAuthTypeFailed(response.detail));
    } else {
        yield put(checkAuthTypeSuccess(response));
    }
}

function* registerLinkedAccount({payload: {idToken, accessToken, wellKnownEndpoint, corp, clientId}}) {
    const options = {
        method: 'POST',
        body: JSON.stringify({
            'token': idToken,
            'access_token': accessToken,
            'well_known_endpoint': wellKnownEndpoint,
            'corporate': corp,
            'client_id': clientId,
        }),
        credentials: 'include',
    };
    const response = yield call(request, api.registerLinkedAccount(), options);
    if (response.error) {
        yield put(loginUserFailed(response.detail));
    } else {
        const {access_token} = response;
        apiClient.setToken(access_token);
        setSession({authenticated: true});
        yield call(fetchUserProfile);
        defaultConfigurations();
        folderConfigurations();
        yield put(loginUserSuccess(response));
    }
}

/**
 * Login the user
 */
function* login({payload: {username, password, rememberMe}}) {
    const data = new FormData();
    data.append('username', username);
    data.append('password', password);

    if (rememberMe) {
        localStorage.setItem(CACHE_KEYS.REMEMBER_USERNAME, username);
    }

    const options = {
        body: data,
        method: 'POST',
        credentials: 'include',
    };

    const response = yield call(request, api.auth(), options);

    if (response.error) {
        yield put(loginUserFailed(response.detail));
    } else {
        const {access_token} = response;
        apiClient.setToken(access_token);
        setSession({authenticated: true});
        yield call(fetchUserProfile);
        defaultConfigurations();
        folderConfigurations();
        yield put(loginUserSuccess(response));
    }
}

/**
 * Logout the user
 */
function* logout({payload: {history}}) {
    try {
        const token = apiClient.getToken();
        if (token) {
            yield call(fetch, api.logout(), {
                method: 'POST',
                headers: {'Authorization': `Bearer ${token}`},
                credentials: 'include',
            });
        }
    } catch (error) {
    } finally {
        apiClient.clearToken();
        setSession(null);
        sessionStorage.clear();
        try {
            history.push('/account/login');
        } catch (e) {
        }
    }
}

/**
 * Register the user
 */
function* register({payload: {fullname, email, password}}) {
    const options = {
        body: JSON.stringify({fullname, email, password}),
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
    };

    try {
        const response = yield call(request, '/users/register', options);
        yield put(registerUserSuccess(response));
    } catch (error) {
        let message;
        switch (error.status) {
            case 500:
                message = 'Internal Server Error';
                break;
            case 401:
                message = 'Invalid credentials';
                break;
            default:
                message = error;
        }
        yield put(registerUserFailed(message));
    }
}

/**
 * forget password
 */
function* forgetPassword({payload: {username}}) {
    const options = {
        body: JSON.stringify({username}),
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
    };

    try {
        const response = yield call(request, '/users/password-reset', options);
        yield put(forgetPasswordSuccess(response.message));
    } catch (error) {
        let message;
        switch (error.status) {
            case 500:
                message = 'Internal Server Error';
                break;
            case 401:
                message = 'Invalid credentials';
                break;
            default:
                message = error;
        }
        yield put(forgetPasswordFailed(message));
    }
}

export function* watchLoginUser(): any {
    yield takeEvery(LOGIN_USER, login);
}

export function* watchLogoutUser(): any {
    yield takeEvery(LOGOUT_USER, logout);
}

export function* watchRegisterUser(): any {
    yield takeEvery(REGISTER_USER, register);
}

export function* watchForgetPassword(): any {
    yield takeEvery(FORGET_PASSWORD, forgetPassword);
}

export function* watchCheckAuthType(): any {
    yield takeEvery(CHECK_AUTH_TYPE, checkAuthType);
}

export function* watchRegisterLinkAccount(): any {
    yield takeEvery(REGISTER_LINKED_ACCOUNT, registerLinkedAccount);
}

export function* watchFetchUserProfile(): any {
    yield takeEvery(FETCH_USER_PROFILE, fetchUserProfile);
}

export function* watchInitAuth(): any {
    yield takeEvery(INIT_AUTH, initAuth);
}

function* authSaga(): any {
    yield all([
        fork(watchLoginUser),
        fork(watchLogoutUser),
        fork(watchRegisterUser),
        fork(watchForgetPassword),
        fork(watchCheckAuthType),
        fork(watchRegisterLinkAccount),
        fork(watchFetchUserProfile),
        fork(watchInitAuth),
    ]);
}

export default authSaga;
